import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { getType } from "mime";

/**
 * ### グッドツリー社連携ファイル API 操作クラス
 */
export default class GoodTreeFiles {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  public async gets(userStore: LoginUser, goodTreeFilesPrimaryKey: string): Promise<GoodTreeFilesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      goodTreeFilesPrimaryKey: goodTreeFilesPrimaryKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.GoodTreeFiles, formatParams);
    const response = await this.commonService.executeGet<GoodTreeFilesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);
    return response.data;
  }

  /**
   * ### S3から一時的に公開設定にしたファイルを取得する
   */
  public async getS3File(userStore: LoginUser, expireHour: number, goodTreeFile: GoodTreeFile): Promise<File> {
    const publicS3Url: string = await this.getS3PublicUrl(userStore, expireHour, goodTreeFile);
    let mime: string | null = this.getMimeType(goodTreeFile.fileName);
    if (mime == null) {
      console.error("GoodTreeFiles::getS3File() mime is null");
      mime = "";
    }
    const blob: any = await this.getBlobObject(publicS3Url, mime);
    blob.lastModifiedDate = new Date();
    const file = new File([blob], goodTreeFile.fileName, { type: mime });
    return file;
  }

  /**
   * ### S3の公開URLを取得する
   */
  private async getS3PublicUrl(userStore: LoginUser, expireHour: number, goodTreeFile: GoodTreeFile): Promise<string> {
    const preUrl = `${RestUrl.S3Url}/${goodTreeFile.bucket}/`;
    const imageKey = String(goodTreeFile.url).replace(preUrl, "");
    const filePath = decodeURIComponent(imageKey);

    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.PublicS3Url, formatParams);
    const queryParams: any = {
      bucketName: goodTreeFile.bucket,
      expireHour: Number(expireHour),
      filePath: filePath
    };
    const response = await this.commonService.executeGet<{ url: string }>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.url;
  }

  /**
   * ### ファイルURLからBLOBオブジェクトを取得する
   */
  private async getBlobObject(publicS3Url: string, mimeType: string): Promise<Blob> {
    const response: any = await window.axios.get(publicS3Url, {
      responseType: "blob",
      headers: { "Content-Type": mimeType }
    });
    return response.data;
  }

  /**
   * ### MimeTypeを取得する
   */
  private getMimeType(fileName: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileName[0] == ".") {
      contentType = getType(`file${fileName}`);
    } else {
      contentType = getType(`${fileName}`);
    }
    return contentType;
  }
}

export interface GoodTreeFilesGetModel {
  /** 請求書 */
  invoices: Array<InvoiceFileByUseOfMonth>;
  /** 請求書 */
  receipts: Array<ReceiptFileByUseOfMonth>;
}

export interface InvoiceFileByUseOfMonth {
  /** 利用月 */
  useOfMonth: number;
  /** ファイル */
  filesByCreated: Array<InvoiceFileByCreated>;
}

export interface InvoiceFileByCreated {
  /** 利用月 */
  useOfMonth: number;
  /** 作成日時 */
  created: number;
  /** ファイル */
  files: Array<GoodTreeFile>;
}

export interface ReceiptFileByUseOfMonth {
  /** 利用月 */
  useOfMonth: number;
  /** ファイル */
  filesByCreated: Array<ReceiptFileByCreated>;
}

export interface ReceiptFileByCreated {
  /** 利用月 */
  useOfMonth: number;
  /** 作成日時 */
  created: number;
  /** ファイル */
  files: Array<GoodTreeFile>;
}

export interface GoodTreeFile {
  /** パーティションキー */
  goodTreeFilesPrimaryKey: string;
  /** 事業所番号 */
  careOfficeNumber: string;
  /** サービス種別 */
  careOfficeServiceType: string;
  /** ソートキー */
  goodTreeFilesSortKey: string;
  /** 文書種別 */
  documentType: DocumentType;
  /** 作成日時 */
  created: number;
  /** 保険者番号 */
  insuredNumber: string;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string;
  /** 利用月（yyyymm 形式） */
  monthOfUse: number;
  /** S3 バケット名 */
  bucket: string;
  /** S3 のファイル URL */
  url: string;
  /** ファイル名 */
  fileName: string;
  /** 論理削除フラグ */
  deleteFlag: boolean;
}

export enum DocumentType {
  /** 未入力 */
  UNKNOWN = "",
  /** 請求書 */
  INVOICE = "請求書"
}
